import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, Checkbox } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { SportBrand } from '@/models/sport-brand.model';
import { getPagedSportBrands } from '@/services/sport-brand.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const SportBrandList = () => {
  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<SportBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: PaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<SportBrand> = await getPagedSportBrands(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching sport brands:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/sport-brands/new`)}>
          Add New Brand
        </Button>

        <Stack spacing={6}>          
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Cover Image</HeaderCell>
          <ImageCell dataKey="coverImageUrl" />
        </Column>              
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Have Shoe</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: SportBrand) => (   
            <Checkbox checked={rowData.haveShoe} readOnly></Checkbox>
          )}
          </Cell>
        </Column>          
        <Column flexGrow={1} align="center" >
          <HeaderCell>Have Cloth</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: SportBrand) => (   
            <Checkbox checked={rowData.haveCloth} readOnly></Checkbox>
          )}
          </Cell>
        </Column>          
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : SportBrand) => (              
              <IconButton onClick={() => nav(`/sport-brands/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default SportBrandList;
